// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Loader2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUDITE_AVATAR = '/audite-avatar.png';

export default function AssistantChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Load history when opening
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen]); // eslint-disable-line

  const loadHistory = async () => {
    try {
      const params = sessionId ? `?session_id=${sessionId}` : '';
      const { data } = await axios.get(`${API}/assistant/history${params}`);
      if (data.messages?.length > 0) {
        setMessages(data.messages.map(m => ({ role: m.role, content: m.content })));
        if (data.session_id) setSessionId(data.session_id);
      }
    } catch (err) {
      // No prior history is a normal state for new users.
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/assistant/chat`, {
        message: text,
        session_id: sessionId,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      if (data.session_id) setSessionId(data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, ocorreu um erro. Tente novamente.' }]);
    }
    setLoading(false);
  };

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/assistant/history`);
    } catch (err) {
      // Local state is still cleared below.
    }
    setMessages([]);
    setSessionId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        data-testid="assistant-fab"
        className="fixed bottom-20 right-5 z-50 w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center justify-center overflow-hidden border-2 border-primary/30"
      >
        <img src={AUDITE_AVATAR} alt="Audite" className="w-full h-full object-cover" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="assistant-chat-panel">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground rounded-t-2xl">
        <img src={AUDITE_AVATAR} alt="Audite" className="w-8 h-8 rounded-full border border-white/30 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Audite</p>
          <p className="text-xs opacity-80">Assistente Virtual JW Audit</p>
        </div>
        <button onClick={handleClear} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" title="Limpar histórico" data-testid="assistant-clear-btn">
          <Trash2 className="w-4 h-4" />
        </button>
        <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-testid="assistant-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <img src={AUDITE_AVATAR} alt="Audite" className="w-16 h-16 mx-auto rounded-full border-2 border-muted mb-3" />
            <p className="text-sm text-muted-foreground">Olá! Sou a Audite, como posso ajudar?</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Pergunte sobre funcionalidades, formulários, documentos...</p>
            <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
              {['Como criar um relatório?', 'Como instalar no celular?', 'O que é S-25b?'].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="text-xs px-2.5 py-1.5 rounded-full border border-border bg-muted/50 hover:bg-muted text-muted-foreground transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={msg.id || `msg-${i}-${msg.role}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-primary text-primary-foreground rounded-br-md'
                : 'bg-muted text-foreground rounded-bl-md'
            }`}>
              <p className="whitespace-pre-wrap break-words">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
              <img src={AUDITE_AVATAR} alt="Audite" className="w-5 h-5 rounded-full" />
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-border bg-card">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua pergunta..."
            className="flex-1 bg-muted rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/50"
            disabled={loading}
            data-testid="assistant-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            size="icon"
            className="rounded-xl h-10 w-10 flex-shrink-0"
            data-testid="assistant-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
